/**
 * RelatedPages - "Related pages" box shown at the bottom of a published article.
 *
 * Ported from the Trinity_01 (Kruthivas) version of this app and adapted to this app's
 * data model: pages live in a nested navigation tree (tabs -> groups -> sub-groups -> pages),
 * may have a parent page (`parent_id`), and carry an explicit `tags` list.
 *
 * Score for every other published page:
 *   +3  filed in the same (deepest) navigation group
 *   +1  filed in the same tab but a different group
 *   +2  per shared tag
 *   +3  direct parent or child page (`parent_id`)
 * Highest scores first, top 5, the current page excluded. Nothing renders when there is
 * no signal in common (no empty box).
 */
import { useMemo } from 'react';
import { Link2, FileText } from 'lucide-react';

const slugOf = (p) => (typeof p === 'string' ? p : p?.page);

// slug -> { tabId, groupPath } by walking the recursive navigation tree.
export function buildNavLocations(navigation) {
  const loc = {};
  const walk = (groups, tabId, parentPath) => {
    (groups || []).forEach((g) => {
      const path = [...parentPath, g.group || g.name || g.title || ''];
      (g.pages || []).forEach((p) => {
        const s = slugOf(p);
        if (s && !loc[s]) loc[s] = { tabId, groupPath: path.join('/') };
      });
      walk(g.groups, tabId, path);
    });
  };
  (navigation?.tabs || []).forEach((t) => walk(t.groups, t.id || t.label || '', []));
  walk(navigation?.groups, '_root', []);
  return loc;
}

const tagSet = (doc) =>
  new Set((doc?.tags || []).map((t) => String(t || '').trim().toLowerCase()).filter(Boolean));

export function computeRelated(currentDoc, documents, navigation, limit = 5) {
  if (!currentDoc || !Array.isArray(documents) || documents.length === 0) return [];
  const locations = buildNavLocations(navigation);
  const here = locations[currentDoc.slug];
  const myTags = tagSet(currentDoc);
  const scored = [];

  for (const doc of documents) {
    if (!doc || doc.slug === currentDoc.slug) continue;
    let score = 0;
    const there = locations[doc.slug];
    if (here && there) {
      if (here.groupPath === there.groupPath && here.tabId === there.tabId) score += 3;
      else if (here.tabId === there.tabId) score += 1;
    }
    for (const t of tagSet(doc)) if (myTags.has(t)) score += 2;
    if (
      (currentDoc.parent_id && currentDoc.parent_id === doc.id) ||
      (doc.parent_id && doc.parent_id === currentDoc.id)
    ) {
      score += 3;
    }
    if (score > 0) scored.push({ doc, score });
  }

  scored.sort((a, b) => b.score - a.score || (a.doc.order ?? 0) - (b.doc.order ?? 0));
  return scored.slice(0, limit).map((s) => s.doc);
}

export const RelatedPages = ({ currentDoc, documents, navigation, onSelect, theme }) => {
  const related = useMemo(
    () => computeRelated(currentDoc, documents, navigation),
    [currentDoc, documents, navigation],
  );
  if (related.length === 0) return null;

  // Markup/classes ported from the Trinity reference RelatedPages (theme object from PublicDocs).
  const isDark = theme?.id === 'dark';

  return (
    <div className={`mt-12 pt-8 border-t ${theme?.border || 'border-gray-200 dark:border-white/10'}`} data-testid="related-pages">
      <h3 className={`text-xs font-semibold uppercase tracking-wider mb-4 flex items-center gap-1.5 ${theme?.textMuted || 'text-gray-500'}`}>
        <Link2 className="w-3.5 h-3.5" />
        Related pages
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {related.map((doc) => (
          <button
            key={doc.slug}
            onClick={() => onSelect(doc.slug)}
            className={`flex items-center gap-2.5 px-4 py-3 rounded-lg border text-left transition-colors ${theme?.border || 'border-gray-200 dark:border-white/10'} ${
              isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
            }`}
            data-testid={`related-page-${doc.slug}`}
          >
            <FileText className={`w-4 h-4 flex-shrink-0 ${theme?.textSecondary || 'text-gray-400'}`} />
            <span className={`text-sm font-medium truncate ${theme?.text || 'text-gray-900 dark:text-white'}`}>{doc.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default RelatedPages;
